import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PRIVACY_BODY = [
  ['Who we are', `Ottawa Volleyball Revival ("OVR") operates ovrleague.com. We organize drop-in volleyball sessions and tournaments. You can reach us at ovrleague@gmail.com.`],
  ['What we collect', `When you create an account or register for an event we collect your name, email address, phone number, and the registrations you sign up for. Payment information is collected and stored by Stripe. We never see or store your card details. If you upload a profile picture, it is stored in our Supabase project.`],
  ['Why we collect it', `We use this information to: confirm your registration, contact you about the event you signed up for, run team rosters and player assignments, issue refunds, and comply with our legal obligations. We do not sell your data to third parties.`],
  ['Who we share it with', `We share the minimum data necessary with these processors: Supabase (account + registration data), Stripe (payments), Vercel (site hosting), Google (OAuth sign-in if you use it), Resend (transactional email). Each processor handles your data under their own privacy terms.`],
  ['Your rights', `You can request a copy of your data, ask us to correct it, or ask us to delete your account by emailing ovrleague@gmail.com. We will respond within 30 days. Deleting your account removes your registration history; payment records may be retained where required by Canadian tax law.`],
  ['Cookies', `We use a small number of cookies to keep you signed in and remember your cart. We do not use third-party advertising cookies.`],
  ['Children', `OVR events are open to all ages, but accounts on this site are for users 13 and over. If you are under 13 a parent or guardian must register on your behalf.`],
  ['Changes', `We may update this policy from time to time. Material changes will be announced on this page with a new effective date.`],
];

const TERMS_BODY = [
  ['Acceptance', `By creating an account or registering for an OVR event you agree to these Terms of Service and our Privacy Policy.`],
  ['Eligibility', `You must be 13 or older to create an account. Anyone under 18 must have parental or guardian consent to participate in OVR events.`],
  ['Registrations and payments', `Registration fees are processed through Stripe in Canadian dollars. Your spot is not confirmed until payment is successfully received. Pricing for each event is shown at checkout and is the authoritative amount.`],
  ['Cancellation and refunds', `Tournament team registrations must be cancelled at least 5 days before the tournament start time to receive a full refund. Tournament free agent registrations must be cancelled at least 2 days before the tournament start time to receive a full refund. Drop-in registrations must be cancelled at least 24 hours before the session start time to receive a full refund. All refunds are issued minus payment processing fees. Free agents who are not placed on a team in time are refunded in full automatically. Refunds are processed back to the original payment method within 5–10 business days.`],
  ['Code of conduct', `Players are expected to compete fairly, respect officials and other players, and follow facility rules. OVR may remove a participant from an event for unsportsmanlike conduct, harassment, intoxication, or any behaviour that endangers other players, without refund.`],
  ['Risk and liability', `Volleyball is a physical sport. You participate at your own risk. OVR is not liable for injuries, lost property, or any indirect or consequential damages arising from your participation. By registering you confirm you are physically fit to play.`],
  ['Media release', `By registering you consent to OVR capturing photos and video during the event and using them on OVR's social media, website, and marketing materials. If you do not consent, notify ovrleague@gmail.com before the event.`],
  ['Account responsibility', `You are responsible for keeping your account credentials secure. Notify us immediately if you suspect unauthorized access.`],
  ['Changes to terms', `We may update these terms from time to time. Continued use of the site after a change means you accept the updated terms.`],
  ['Contact', `Questions about these terms: ovrleague@gmail.com.`],
];

export default function LegalPage({ kind }) {
  const isPrivacy = kind === 'privacy';
  const title = isPrivacy ? 'PRIVACY POLICY' : 'TERMS OF SERVICE';
  const body = isPrivacy ? PRIVACY_BODY : TERMS_BODY;
  const effective = '15 May 2026';

  // Force scrolling on. App.js locks body overflow for the homepage; that
  // state persists in SPA navigation so we explicitly unlock it here.
  useEffect(() => {
    const root = document.getElementById('root');
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevHtmlHeight   = document.documentElement.style.height;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyHeight   = document.body.style.height;
    const prevRootOverflow = root ? root.style.overflow : '';
    const prevRootHeight   = root ? root.style.height : '';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    window.scrollTo(0, 0);
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.height = prevHtmlHeight;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.height = prevBodyHeight;
      if (root) { root.style.overflow = prevRootOverflow; root.style.height = prevRootHeight; }
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      color: '#e8e8e8',
      fontFamily: "'Inter', system-ui, sans-serif",
      padding: '60px 24px 80px',
    }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <Link to="/" style={{
          fontFamily: "'Bebas Neue','Oswald',sans-serif",
          fontSize: 13, letterSpacing: '0.16em',
          color: '#9D1515', textDecoration: 'none',
        }}>← OVR LEAGUE</Link>

        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 11, fontWeight: 700, letterSpacing: '0.22em',
          color: '#9D1515', margin: '40px 0 12px', textTransform: 'uppercase',
        }}>
          Ottawa Volleyball Revival
        </p>
        <h1 style={{
          fontFamily: "'Bebas Neue','Oswald',sans-serif",
          fontSize: 'clamp(48px, 8vw, 84px)',
          color: '#fff', margin: 0, letterSpacing: '0.03em', lineHeight: 0.95,
        }}>
          {title}
        </h1>
        <p style={{ fontSize: 12, color: '#666', marginTop: 10, letterSpacing: '0.06em' }}>
          Effective {effective}
        </p>

        <div style={{ marginTop: 50, borderTop: '1px solid #1f1f1f' }}>
          {body.map(([heading, text], i) => (
            <section key={i} style={{ padding: '28px 0', borderBottom: '1px solid #1f1f1f' }}>
              <h2 style={{
                fontFamily: "'Bebas Neue','Oswald',sans-serif",
                fontSize: 22, color: '#fff', margin: '0 0 12px',
                letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>{heading}</h2>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: 'rgba(255,255,255,0.78)', margin: 0 }}>
                {text}
              </p>
            </section>
          ))}
        </div>

        <div style={{ marginTop: 40, fontSize: 12, color: '#555' }}>
          Questions? Email <a href="mailto:ovrleague@gmail.com" style={{ color: '#9D1515', textDecoration: 'none' }}>ovrleague@gmail.com</a>.
        </div>
      </div>
    </div>
  );
}
